import axios from 'axios';
import { getAuthHeader } from './authService';
import API_CONFIG from '../config';

const API_URL = `${API_CONFIG.BASE_URL}/taxes`;

/**
 * Service for managing tax rates
 */
const taxService = {
  /**
   * Get all tax rates with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.size - Page size
   * @param {string} params.sort - Field to sort by
   * @param {string} params.direction - Sort direction (ASC or DESC)
   * @returns {Promise<Object>} Paginated tax data
   */
  getAllTaxes: async (params = { page: 0, size: 10, sort: 'name', direction: 'ASC' }) => {
    try {
      const response = await axios.get(API_URL, {
        params,
        headers: getAuthHeader()
      });
      
      // Handle both paginated and non-paginated responses
      if (response.data && typeof response.data === 'object' && response.data.content) {
        // Return paginated response
        return response.data;
      } else if (Array.isArray(response.data)) {
        // If API returns an array directly (non-paginated)
        // Convert to paginated format for consistent handling in the UI
        return {
          content: response.data,
          totalElements: response.data.length,
          totalPages: 1,
          size: response.data.length,
          number: 0
        };
      } else {
        // Handle unexpected response format
        console.warn('Unexpected response format from taxes API:', response.data);
        return { content: [], totalElements: 0, totalPages: 0, size: params.size, number: params.page };
      }
    } catch (error) {
      console.error('Error fetching taxes:', error);
      throw error;
    }
  },

  /**
   * Get a tax by ID
   * @param {number} id - Tax ID
   * @returns {Promise<Object>} Tax details
   */
  getTaxById: async (id) => {
    try {
      const response = await axios.get(`${API_URL}/${id}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching tax with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new tax
   * @param {Object} taxData - Tax data
   * @returns {Promise<Object>} Created tax
   */
  createTax: async (taxData) => {
    try {
      const response = await axios.post(API_URL, taxData, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating tax:', error);
      throw error;
    }
  },

  /**
   * Update a tax
   * @param {number} id - Tax ID
   * @param {Object} taxData - Updated tax data
   * @returns {Promise<Object>} Updated tax
   */
  updateTax: async (id, taxData) => {
    try {
      const response = await axios.put(`${API_URL}/${id}`, taxData, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating tax with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a tax
   * @param {number} id - Tax ID
   * @returns {Promise<void>}
   */
  deleteTax: async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: getAuthHeader()
      });
    } catch (error) {
      console.error(`Error deleting tax with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get tax rates by company ID
   * @param {number} companyId - Company ID
   * @returns {Promise<Array>} List of tax rates
   */
  getTaxesByCompany: async (companyId) => {
    try {
      const response = await axios.get(`${API_URL}/company/${companyId}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching taxes for company ID ${companyId}:`, error);
      throw error;
    }
  }
};

export default taxService; 